import {
  faEyeSlash,
  faMegaphone,
  faMessageQuestion,
  faPaperPlane,
  faPenToSquare,
  faTrash,
  faUserMinus,
  faUserPlus,
  faWrench
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { Link } from 'react-router-dom'

import PropTypes from 'prop-types'
import { useContext, useMemo, useState } from 'react'

import {
  faEye,
  faGhost,
  faHammer,
  faReply,
  faThumbTack
} from '@fortawesome/pro-solid-svg-icons'

import { updatePrivateMessageUser } from '@moal/api'
import { MEMBERS, MODERATORS, TRADES } from '../../../constants/chatRooms'
import * as FEATURES from '../../../constants/features'
import * as SETTINGS from '../../../constants/settings'
import { AuthenticationContext } from '../../../context/authenticationContext'
import { SiteConfigContext } from '../../../context/siteConfigContext'
import { markAsQuestion } from '../../../utils/APIs/publicMessages'
import { hasStaffPrivilege } from '../../../utils/RequireRole'
import DeleteModal from './DeleteModal'
import EditModal from './EditModal'
import HighlightModal from './HighlightModal'
import ModModal from './ModModal'
import ResendModal from './ResendModal'
import SendPMToEveryoneModal from './SendPMToEveryoneModal'

const MessageWrapper = ({
  message,
  channel,
  modView = false,
  children,
  dispatch = null,
  history
}) => {
  const { [SETTINGS.FEATURES.key]: enabledFeatures } =
    useContext(SiteConfigContext)
  const questionsEnabled = useMemo(
    () => enabledFeatures && enabledFeatures[FEATURES.QUESTIONS],
    [enabledFeatures]
  )
  const { user } = useContext(AuthenticationContext)
  const [highlight, setHighlight] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [modModal, setModModal] = useState(false)
  const [resendModal, setResendModal] = useState(false)
  const [highlightModal, setHighlightModal] = useState(false)
  const [sendPMToEveryoneModal, setSendPMToEveryoneModal] = useState(false)

  const handleHighlight = (shouldHighlight) => {
    setHighlight(shouldHighlight)
  }

  if (!modView) {
    return children
  }

  const ENABLED = {
    EDIT:
      channel === TRADES ||
      (hasStaffPrivilege(user) && !history) ||
      message.toEveryone,
    DELETE: !history || message.toEveryone,
    MODUSER: channel === MEMBERS && hasStaffPrivilege(user),
    RESEND: channel === TRADES,
    PRIVATEMESSAGES: channel === MODERATORS && history,
    QUESTIONS: questionsEnabled
  }

  return (
    <div className="flex gap-2 w-full items-start break-words">
      {channel === MEMBERS ? (
        <MembersMessage
          highlight={highlight}
          message={message}
          handleHighlight={handleHighlight}
        >
          {children}
        </MembersMessage>
      ) : (
        children
      )}
      <ModControls
        ENABLED={ENABLED}
        channel={channel}
        history={history}
        message={message}
        setDeleteModal={setDeleteModal}
        setEditModal={setEditModal}
        setHighlightModal={setHighlightModal}
        setModModal={setModModal}
        setResendModal={setResendModal}
        setSendPMToEveryoneModal={setSendPMToEveryoneModal}
      />
      {ENABLED.EDIT && (
        <EditModal
          open={editModal}
          setOpen={setEditModal}
          message={message}
          channel={channel}
          dispatch={dispatch}
        />
      )}
      {ENABLED.DELETE && (
        <DeleteModal
          open={deleteModal}
          setOpen={setDeleteModal}
          message={message}
          channel={channel}
          dispatch={dispatch}
        />
      )}

      {ENABLED.MODUSER && (
        <>
          <ModModal open={modModal} setOpen={setModModal} message={message} />
          <HighlightModal
            open={highlightModal}
            setOpen={setHighlightModal}
            message={message}
            dispatch={dispatch}
          />
        </>
      )}
      {ENABLED.RESEND && (
        <ResendModal
          open={resendModal}
          setOpen={setResendModal}
          message={message}
        />
      )}
      {ENABLED.PRIVATEMESSAGES && (
        <>
          <HighlightModal
            open={highlightModal}
            setOpen={setHighlightModal}
            message={message}
            dispatch={dispatch}
          />
          <SendPMToEveryoneModal
            open={sendPMToEveryoneModal}
            setOpen={setSendPMToEveryoneModal}
            message={message}
            dispatch={dispatch}
          />
        </>
      )}
    </div>
  )
}

const MembersMessage = ({ highlight, message, handleHighlight, children }) => {
  const highlightClasses = highlight
    ? 'bg-neutral-100 dark:bg-neutral-800 border-neutral-300 dark:border-neutral-700'
    : 'border-transparent'
  return (
    <div
      className={`flex flex-1 border-x-2 w-full ${highlightClasses}`}
      onMouseEnter={() => handleHighlight(true)}
      onMouseLeave={() => handleHighlight(false)}
    >
      {message.user.moderation.shadowbanned && (
        <div className="flex items-center justify-around bg-neutral-50 dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 p-1 my-1 ml-2 text-white uppercase font-bold rounded">
          <FontAwesomeIcon
            className="h-3.5 text-black dark:text-white"
            icon={faGhost}
          />
        </div>
      )}
      {children}
    </div>
  )
}

const ModControls = ({
  ENABLED,
  channel,
  history,
  message,
  setDeleteModal,
  setEditModal,
  setHighlightModal,
  setModModal,
  setResendModal,
  setSendPMToEveryoneModal
}) => {
  return (
    <Menu as="div" className="relative inline-block text-left pr-2">
      <div className="">
        <MenuButton
          className="rounded-full size-8 flex items-center justify-center button-outline"
          title="Mod Controls"
        >
          <span className="sr-only">Mod Controls</span>
          <FontAwesomeIcon
            icon={faWrench}
            className="size-4"
            aria-hidden="true"
          />
        </MenuButton>
      </div>

      <MenuItems className="absolute right-5 top-1 -translate-y-full z-10 mt-2 min-w-fit w-32 origin-bottom-right rounded-md bg-white shadow-lg ring-1 ring-black/20 dark:ring-black/50 focus:outline-none dark:bg-neutral-900">
        <div className="py-1">
          <PrivateMessageButtons
            message={message}
            setSendPMToEveryoneModal={setSendPMToEveryoneModal}
            show={ENABLED.PRIVATEMESSAGES}
          />
          <PinMessageButton message={message} show={ENABLED.MODUSER} />
          <HighlightMessageButton
            message={message}
            setHighlightModal={setHighlightModal}
          />
          {ENABLED.QUESTIONS && channel === MEMBERS && (
            <QuestionAndAnswerButton message={message} />
          )}
          <ModUserButton
            history={history}
            message={message}
            setModModal={setModModal}
            show={ENABLED.MODUSER}
          />
          <EditButton setEditModal={setEditModal} show={ENABLED.EDIT} />
          <ResendButton
            history={history}
            setResendModal={setResendModal}
            show={ENABLED.RESEND}
          />
          <DeleteButton setDeleteModal={setDeleteModal} show={ENABLED.DELETE} />
        </div>
      </MenuItems>
    </Menu>
  )
}

const EditButton = ({ setEditModal, show }) => {
  if (!show) {
    return null
  }

  return (
    <MenuItem>
      <button
        className="mod-button"
        onClick={() => setEditModal(true)}
        title="Edit Message"
        onMouseEnter={(e) => e.target.focus()}
        onMouseLeave={(e) => e.target.blur()}
      >
        <FontAwesomeIcon className="size-4" icon={faPenToSquare} />
        <span>Edit</span>
      </button>
    </MenuItem>
  )
}

const DeleteButton = ({ setDeleteModal, show }) => {
  if (!show) {
    return null
  }

  return (
    <MenuItem>
      <button
        className="mod-button text-red-500"
        onClick={() => setDeleteModal(true)}
        title="Delete Message"
        onMouseEnter={(e) => e.target.focus()}
        onMouseLeave={(e) => e.target.blur()}
      >
        <FontAwesomeIcon className="size-4" icon={faTrash} />
        <span>Delete</span>
      </button>
    </MenuItem>
  )
}

const HighlightMessageButton = ({ message, setHighlightModal }) => {
  return (
    <MenuItem>
      <button
        className="mod-button"
        onClick={() => {
          setHighlightModal(true)
        }}
        title="Show on stream"
        onMouseEnter={(e) => e.target.focus()}
        onMouseLeave={(e) => e.target.blur()}
      >
        <FontAwesomeIcon className={`size-4`} icon={faMegaphone} />
        <span>Show on Stream</span>
      </button>
    </MenuItem>
  )
}

const QuestionAndAnswerButton = ({ message }) => {
  return (
    <MenuItem>
      <button
        className="mod-button"
        onClick={() => {
          handleAddToQuestionAndAnswer(message)
        }}
      >
        <FontAwesomeIcon className={`size-4`} icon={faMessageQuestion} />
        <span>Add to Q&A</span>
      </button>
    </MenuItem>
  )
}

const ModUserButton = ({ setModModal, show }) => {
  if (!show) {
    return null
  }

  return (
    <MenuItem>
      <button
        className="mod-button"
        onClick={() => setModModal(true)}
        title="Moderate User"
        onMouseEnter={(e) => e.target.focus()}
        onMouseLeave={(e) => e.target.blur()}
      >
        <FontAwesomeIcon className="size-4" icon={faHammer} />
        <span>Quick Mod</span>
      </button>
    </MenuItem>
  )
}

const ResendButton = ({ setResendModal, show }) => {
  if (!show) {
    return null
  }

  return (
    <MenuItem>
      <button
        className="mod-button"
        onClick={() => setResendModal(true)}
        title="Resend Notification"
        onMouseEnter={(e) => e.target.focus()}
        onMouseLeave={(e) => e.target.blur()}
      >
        <FontAwesomeIcon className="size-4" icon={faPaperPlane} />
        <span>Resend</span>
      </button>
    </MenuItem>
  )
}

const PrivateMessageButtons = ({ show, message, setSendPMToEveryoneModal }) => {
  if (!show) {
    return null
  }

  const pinUser = async (user) => {
    await updatePrivateMessageUser({
      token: localStorage.getItem('authToken'),
      id: user._id,
      data: { pinned: !message.user.pinned }
    })
  }

  const readUser = async (user) => {
    await updatePrivateMessageUser({
      token: localStorage.getItem('authToken'),
      id: user._id,
      data: { awaitingReply: !message.user.awaitingReply }
    })
  }

  return (
    <>
      <MenuItem>
        <Link
          className="no-underline"
          to={`/mod/private-messages?id=${message.user.userService._id}`}
        >
          <button
            className="mod-button"
            title="Reply To User"
            onMouseEnter={(e) => e.target.focus()}
            onMouseLeave={(e) => e.target.blur()}
          >
            <FontAwesomeIcon className="size-4" icon={faReply} />
            <span>Reply To User</span>
          </button>
        </Link>
      </MenuItem>
      <MenuItem>
        <button
          className="mod-button"
          onClick={() => readUser(message.user)}
          title={`${message.user.awaitingReply ? 'Mark Read' : 'Mark Unread'}`}
          onMouseEnter={(e) => e.target.focus()}
          onMouseLeave={(e) => e.target.blur()}
        >
          <FontAwesomeIcon
            className="size-4"
            icon={message.user.awaitingReply ? faEye : faEyeSlash}
          />
          <span>
            {message.user.awaitingReply ? 'Mark Read' : 'Mark Unread'}
          </span>
        </button>
      </MenuItem>
      <MenuItem>
        <button
          className="mod-button"
          onClick={() => pinUser(message.user)}
          title={`${message.user.awaitingReply ? 'Pin User' : 'Unpin User'}`}
          onMouseEnter={(e) => e.target.focus()}
          onMouseLeave={(e) => e.target.blur()}
        >
          <FontAwesomeIcon
            className="size-4"
            icon={message.user.pinned ? faUserMinus : faUserPlus}
          />
          <span>{message.user.pinned ? 'Unpin User' : 'Pin User'}</span>
        </button>
      </MenuItem>
      <hr />
      <MenuItem>
        <button
          className="mod-button"
          onClick={() => setSendPMToEveryoneModal(true)}
          title="Send Private Message  to Everyone"
          onMouseEnter={(e) => e.target.focus()}
          onMouseLeave={(e) => e.target.blur()}
        >
          <FontAwesomeIcon className={`size-4`} icon={faMegaphone} />
          <span>Send PM to Everyone</span>
        </button>
      </MenuItem>
    </>
  )
}

const PinMessageButton = ({ message, show }) => {
  const { updateSetting } = useContext(SiteConfigContext)
  const handlePin = async () => {
    await updateSetting(SETTINGS.PINNED_MESSAGE, message)
  }

  if (!show) {
    return null
  }

  return (
    <MenuItem>
      <button
        className="mod-button"
        onClick={handlePin}
        title="Pin Message"
        onMouseEnter={(e) => e.target.focus()}
        onMouseLeave={(e) => e.target.blur()}
      >
        <FontAwesomeIcon className="size-4 rotate-45" icon={faThumbTack} />
        <span>Pin Message</span>
      </button>
    </MenuItem>
  )
}

const handleAddToQuestionAndAnswer = async (message) => {
  await markAsQuestion(message._id)
}

MessageWrapper.propTypes = {
  message: PropTypes.object.isRequired,
  channel: PropTypes.string.isRequired,
  children: PropTypes.any.isRequired,
  index: PropTypes.number.isRequired,
  modView: PropTypes.bool
}

export default MessageWrapper
